import { findAncestors, findChildren, findDescendants, findParent } from 'common/utils/hierarchy';

import { TreeSelectOption } from './types';

export function getOptionAncestors(
  selectedOption: TreeSelectOption,
  options: TreeSelectOption[],
): TreeSelectOption[] {
  const ancestors = findAncestors(selectedOption, options);
  const selectedOptionExists = options.find(({ value }) => value === selectedOption.value);

  if (!selectedOptionExists) {
    return [];
  }

  if (!selectedOption.checked) {
    return ancestors.map((option) => ({ ...option, checked: false }));
  }

  const optionAncestors: TreeSelectOption[] = [];
  if (selectedOption.checked) {
    const stack = [selectedOption];

    while (stack.length) {
      const current = stack.pop();

      if (current) {
        const parent = findParent(current, ancestors);

        if (parent) {
          const siblings = options.filter(
            ({ parentValue, value }) => parentValue === parent.value && value !== current.value,
          );

          stack.push({
            ...parent,
            checked: siblings.every(({ checked }) => checked) && current.checked,
          });
        }
        const chidren = findChildren(current.value, options);
        const areAllChildrenChecked = chidren.every(({ checked }) => checked);
        const currentOptionParsed = optionAncestors.find(({ value }) => value === current.value);
        const isSelectedOption = selectedOption.value === current.value;

        if (!currentOptionParsed && !isSelectedOption) {
          const nextChecked = current.checked || areAllChildrenChecked;
          optionAncestors.push({ ...current, checked: nextChecked });
        }
      }
    }
  }
  return optionAncestors;
}

function getTreeOptions(options: TreeSelectOption[]): TreeSelectOption[] {
  // removes checked from children if parent is selected
  // used to convert form state to api-friendly shortened list
  return options.map((option) => {
    const parent = findParent(option, options);

    if (parent && parent.checked) {
      return {
        ...option,
        checked: false,
      };
    }

    return option;
  });
}

export function initializeTreeOptions(options: TreeSelectOption[]): TreeSelectOption[] {
  // converts api-friendly shortened list into form state
  return options.map((option) => {
    const ancestors = findAncestors(option, options);
    const checkedAncestor = ancestors.find(({ checked }) => checked);

    if (ancestors.length && checkedAncestor) {
      return {
        ...option,
        checked: true,
      };
    }

    return option;
  });
}

function getOptionDescendants(nextOption: TreeSelectOption, options: TreeSelectOption[]) {
  const result = findDescendants(nextOption.value, options).map((option) => ({
    ...option,
    checked: nextOption.checked,
  }));
  return result;
}

export function getTreeValues(selectedValues: string[], options: TreeSelectOption[]) {
  const _options = options.map((option) => ({
    ...option,
    checked: selectedValues.includes(option.value),
  }));
  const treeOptions = getTreeOptions(_options);
  return treeOptions.filter(({ checked }) => checked).map(({ value }) => value);
}

export function getReducedCheckedOptions(options: TreeSelectOption[]) {
  return getTreeOptions(options).filter(({ checked }) => checked);
}

export function getCheckedOptionValues(
  selectedOption: TreeSelectOption,
  options: TreeSelectOption[],
): string[] {
  const descendantOptions = getOptionDescendants(selectedOption, options);
  const ancestorOptions = getOptionAncestors(selectedOption, options);
  const affectedOptions = [selectedOption, ...descendantOptions, ...ancestorOptions];

  return options
    .map((option) => {
      const affectedOption = affectedOptions.find(({ value }) => value === option.value);
      if (affectedOption) {
        return affectedOption;
      }

      return option;
    })
    .filter(({ checked }) => checked)
    .map(({ value }) => value);
}

function getFilteredOptions(search: string, options: TreeSelectOption[]) {
  const trimmedSearch = search.trim().toLowerCase();

  if (!trimmedSearch) {
    return options;
  }

  return options.filter((option) => {
    const isEqual = option.label.toLowerCase().includes(trimmedSearch);

    const isEqualWithAncestors = findAncestors(option, options)
      .reverse()
      .concat([option])
      .map((o) => o.label)
      .join(' › ')
      .toLowerCase()
      .includes(trimmedSearch);

    return isEqual || isEqualWithAncestors;
  });
}

export function getSearchResults(search: string, options: TreeSelectOption[]) {
  const trimmedSearch = search.trim().toLowerCase();

  if (!trimmedSearch) {
    return options;
  }

  const searchResults = getFilteredOptions(trimmedSearch, options);

  // list of options from every search result family tree
  return searchResults.reduce<TreeSelectOption[]>((acc, searchResult) => {
    const ancestors = findAncestors(searchResult, options);
    const descendants = findDescendants(searchResult.value, options);
    const filtered = [searchResult, ...ancestors, ...descendants].filter(
      (filteredOption) => !acc.find(({ value }) => value === filteredOption.value),
    );
    acc.push(...filtered);
    return acc;
  }, []);
}

export function getTagLabelWithAncestors(
  option: TreeSelectOption,
  // `ancestorOptions` is expected to be sorted: oldest ancestor first
  ancestorOptions: TreeSelectOption[] = [],
): string {
  let result = '';

  for (const ancestor of ancestorOptions) {
    result += `${ancestor.label} > `;
  }

  return `${result}${option.label}`;
}
